// Actions tab — read-only Jira mirror (WP-H rev 2). No local done-checkbox;
// status/assignee come from action.jira. Raise / New+Raise for unraised rows.
import { useState } from "react";
import { useSnackbar } from "notistack";
import { Card, Button, TextField, EmptyState, Pill } from "../../design/ui";
import {
  createVisitAction, type VisitAction, type RaiseTarget,
} from "../../lib/brain-api";
import { RaiseTicketDialog, type RaiseSubject } from "./RaiseTicketDialog";

const JIRA_BROWSE = "https://aeronetnz.atlassian.net/browse";

function statusTone(cat: string | undefined): { bg: string; color: string } {
  if (cat === "done") return { bg: "var(--color-background-success)", color: "var(--kt-status-active)" };
  if (cat === "indeterminate") return { bg: "var(--kt-teal-tint, #d9ede8)", color: "var(--kt-teal-ink)" };
  return { bg: "var(--color-background-warning)", color: "var(--kt-status-paused)" };
}

export function ActionsTab({
  customerId, visitId, actions, onChanged,
}: {
  customerId: string;
  visitId: string;
  actions: VisitAction[];
  onChanged: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const accepted = actions.filter((a) => a.reviewState === "accepted");
  const [text, setText] = useState("");
  const [owner, setOwner] = useState("");
  const [creating, setCreating] = useState(false);
  const [raising, setRaising] = useState<{ subject: RaiseSubject; target: RaiseTarget } | null>(null);

  const openRaise = (action: VisitAction) => {
    const raw = action.suggestedTarget;
    const target: RaiseTarget =
      raw === "support" || raw === "feature-request" || raw === "dev" ? raw : "support";
    setRaising({ subject: { kind: "action", action }, target });
  };

  const newAndRaise = async () => {
    if (!text.trim()) return;
    setCreating(true);
    try {
      const action = await createVisitAction({
        customerId,
        visitId,
        text: text.trim(),
        ...(owner.trim() ? { owner: owner.trim() } : {}),
      });
      setText("");
      setOwner("");
      onChanged();
      openRaise(action);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Couldn't create action — try again", { variant: "error" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <div className="kt-record-label" style={{ marginBottom: 10 }}>
        Actions{accepted.length ? ` · ${accepted.length}` : ""}
      </div>
      {accepted.length === 0 ? (
        <EmptyState icon="list-check" title="No accepted actions yet">
          Review AI suggestions on Overview, or add one below and raise it to Jira.
        </EmptyState>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {accepted.map((a) => {
            const jira = a.jira;
            const tone = statusTone(jira?.statusCategory);
            return (
              <div
                key={a.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
                  borderBottom: "1px solid var(--kt-row-divider)", flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13.5, color: "var(--kt-ink)" }}>{jira?.summary ?? a.text}</div>
                  {(a.owner || jira?.assignee) && (
                    <div style={{ fontSize: 12, color: "var(--kt-text-muted)", marginTop: 2 }}>
                      {jira?.assignee ? `Assignee ${jira.assignee}` : a.owner ? `Owner ${a.owner}` : ""}
                    </div>
                  )}
                </div>
                {a.jiraKey ? (
                  <>
                    <a
                      href={`${JIRA_BROWSE}/${a.jiraKey}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--kt-teal-ink)", textDecoration: "none" }}
                    >
                      {a.jiraKey}
                    </a>
                    {jira?.status && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999,
                        background: tone.bg, color: tone.color,
                      }}>
                        {jira.status}
                      </span>
                    )}
                    {!jira && <Pill>Jira sync pending</Pill>}
                  </>
                ) : (
                  <Button size="sm" icon="ticket" onClick={() => openRaise(a)}>Raise</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <TextField placeholder="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} style={{ flex: "0 1 140px" }} />
        <TextField
          placeholder="New action…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void newAndRaise(); }}
          style={{ flex: "1 1 220px" }}
        />
        <Button icon="ticket" disabled={creating || !text.trim()} onClick={() => void newAndRaise()}>
          {creating ? "Creating…" : "New + Raise"}
        </Button>
      </div>

      {raising && (
        <RaiseTicketDialog
          key={raising.subject.kind === "action" ? raising.subject.action.id : raising.subject.item.id}
          customerId={customerId}
          visitId={visitId}
          subject={raising.subject}
          defaultTarget={raising.target}
          onClose={() => setRaising(null)}
          onChanged={() => { setRaising(null); onChanged(); }}
        />
      )}
    </Card>
  );
}
